import { supabaseClient } from '../config/supabase';
import { monitoringService } from './monitoringService';

interface LogEntry {
  level: 'info' | 'warn' | 'error';
  message: string;
  data?: any;
  timestamp: string;
  userId?: string;
}

class LoggingService {
  async saveLog(entry: LogEntry) {
    try {
      // Validate UUID
      if (entry.userId && !this.isValidUUID(entry.userId)) {
        console.warn('Invalid UUID provided for logging:', entry.userId);
        entry.userId = undefined;
      }

      const { error } = await supabaseClient
        .from('logs')
        .insert([{
          level: entry.level,
          message: entry.message,
          data: entry.data,
          user_id: entry.userId,
          timestamp: entry.timestamp
        }]);

      if (error) throw error;
    } catch (err) {
      console.error('Failed to save log:', err);
      monitoringService.trackError(err as Error, {
        action: 'saveLog',
        additionalData: { logEntry: entry }
      });
    }
  }

  private isValidUUID(uuid: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  }
  info(message: string, data?: any, userId?: string) {
    const entry: LogEntry = {
      level: 'info',
      message,
      data,
      userId,
      timestamp: new Date().toISOString()
    };

    this.saveLog(entry);
    
    if (import.meta.env.DEV) {
      console.log(`[INFO] ${message}`, data);
    }
  }

  warn(message: string, data?: any, userId?: string) {
    const entry: LogEntry = {
      level: 'warn',
      message,
      data,
      userId,
      timestamp: new Date().toISOString()
    };

    this.saveLog(entry);
    
    if (import.meta.env.DEV) {
      console.warn(`[WARN] ${message}`, data);
    }
  }

  error(message: string, error: Error | any, userId?: string) {
    const entry: LogEntry = {
      level: 'error',
      message,
      data: {
        error: error instanceof Error ? {
          message: error.message,
          stack: error.stack,
          name: error.name
        } : error
      },
      userId,
      timestamp: new Date().toISOString()
    };

    this.saveLog(entry);
    
    if (import.meta.env.DEV) {
      console.error(`[ERROR] ${message}`, error);
    }
  }
}

export const loggingService = new LoggingService();
